/**
 * Profils d'accès (tasks/acces-copycampers-plan.md) — règles partagées par
 * l'API, le temps réel et le jeu (sous-chemin `@campus/core/access` côté
 * serveur, jamais le barrel).
 *
 * Un membre peut avoir un profil qui le limite à quelques cartes (ex.
 * « Copycampers » : le bâtiment Copy Camp). Le profil et ses cartes sont dans
 * le JWT (`ap`, `az`) : changer le profil d'un membre, ou les cartes d'un
 * profil, déconnecte les personnes concernées (nouveau jeton à la reconnexion).
 *
 * Accès « en plus » (2026-11-01) : un profil `extra` réserve ses cartes à ses
 * membres (ex. « Ascension » : le bâtiment Ascension). Le jeton porte les
 * cartes réservées que la personne n'a pas (`ax`) ; un membre limité reçoit
 * en plus dans `az` les cartes réservées qu'il a.
 *
 * Accès par salle (2026-11-02) : un accès « en plus » peut aussi réserver des
 * salles précises (`roomIds`, ids de `map_zones`), ex. « Ascension Ads ». Le
 * jeton porte les salles réservées que la personne n'a pas (`xr`).
 */

export type AccessProfileKind = 'limit' | 'extra';

export interface AccessClaims {
  ap?: string | undefined;
  az?: string[] | undefined;
  /** Cartes réservées par un accès « en plus » que la personne n'a pas. */
  ax?: string[] | undefined;
  /** Salles (map_zones.id) réservées par un accès « en plus » que la personne n'a pas. */
  xr?: string[] | undefined;
}

/**
 * Champs d'accès du jeton d'une personne. `limit` : son profil limitant (ou
 * null) ; `extras` : ses accès « en plus » ; `reserved` : toutes les cartes
 * réservées du tenant. Les OWNER/ADMIN n'ont aucune restriction.
 */
export function buildAccessClaims(
  role: string,
  limit: { id: string; zoneSlugs: string[] } | null,
  extras: { zoneSlugs: string[]; roomIds?: string[] }[],
  reserved: string[],
  reservedRooms: string[] = [],
): { ap?: string; az?: string[]; ax?: string[]; xr?: string[] } {
  if (role === 'OWNER' || role === 'ADMIN') return {};
  const granted = new Set(extras.flatMap((e) => e.zoneSlugs));
  const ax = [...new Set(reserved)].filter((s) => !granted.has(s));
  const grantedRooms = new Set(extras.flatMap((e) => e.roomIds ?? []));
  const xr = [...new Set(reservedRooms)].filter((r) => !grantedRooms.has(r));
  return {
    ...(limit ? { ap: limit.id, az: [...new Set([...limit.zoneSlugs, ...granted])] } : {}),
    ...(ax.length ? { ax } : {}),
    ...(xr.length ? { xr } : {}),
  };
}

/** Peut-on entrer dans cette salle (bulle ou salle de réunion, id de map_zones) ? */
export function canEnterRoom(claims: AccessClaims | null | undefined, roomId: string): boolean {
  return !(Array.isArray(claims?.xr) && claims.xr.includes(roomId));
}

/** Le jeton limite-t-il les cartes ? */
export function isRestricted(claims: AccessClaims | null | undefined): boolean {
  return !!claims?.ap;
}

/** Peut-on entrer sur cette carte (salle temps réel, vidéo, liste des cartes, bureaux) ? */
export function canEnterZone(claims: AccessClaims | null | undefined, zoneSlug: string): boolean {
  if (Array.isArray(claims?.ax) && claims.ax.includes(zoneSlug)) return false;
  if (!claims?.ap) return true;
  return Array.isArray(claims.az) && claims.az.includes(zoneSlug);
}

/** Fil de chat d'un profil (le général : `{tenantId}:general`). */
export function profileChannelKey(tenantId: string, profileId: string): string {
  return `${tenantId}:profile:${profileId}`;
}

export function generalChannelKey(tenantId: string): string {
  return `${tenantId}:general`;
}

/** Profil d'un fil de chat, null pour le général ou un autre fil. */
export function profileOfChannel(tenantId: string, channelKey: string): string | null {
  const prefix = `${tenantId}:profile:`;
  return channelKey.startsWith(prefix) ? channelKey.slice(prefix.length) || null : null;
}

/**
 * Fil de chat lisible par cette personne : le sien (son profil, ou le général
 * sans profil) ; les OWNER/ADMIN lisent tous les fils du tenant (modération).
 */
export function canUseChannel(
  tenantId: string,
  user: { role: string; ap?: string | undefined },
  channelKey: string,
): boolean {
  const profile = profileOfChannel(tenantId, channelKey);
  const isGeneral = channelKey === generalChannelKey(tenantId);
  if (!isGeneral && !profile) return false;
  if (user.role === 'OWNER' || user.role === 'ADMIN') return true;
  return user.ap ? profile === user.ap : isGeneral;
}

export interface ConnectionParty {
  role: string;
  /** Profil d'accès, null = tout le campus. */
  profileId: string | null;
}

/**
 * Un message privé entre ces deux personnes demande-t-il une connexion acceptée ?
 * Non si l'une est OWNER/ADMIN ou si elles ont le même périmètre (même profil,
 * ou aucune des deux) ; oui sinon (ex. un Copycamper et un membre de Copyhouse).
 */
export function dmNeedsConnection(a: ConnectionParty, b: ConnectionParty): boolean {
  const admin = (r: string) => r === 'OWNER' || r === 'ADMIN';
  if (admin(a.role) || admin(b.role)) return false;
  return (a.profileId ?? null) !== (b.profileId ?? null);
}

export type ConnectionStatus = 'pending' | 'accepted' | 'declined';

/** Demandes de connexion envoyées par jour et par personne. */
export const CONNECTION_REQUESTS_PER_DAY = 20;
/** Délai avant de renvoyer une demande refusée. */
export const CONNECTION_RETRY_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

export const ACCESS_PROFILE_NAME_MAX = 40;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

export interface AccessProfileInput {
  kind: AccessProfileKind;
  name: string;
  zoneSlugs: string[];
  /** Salles réservées (accès « en plus » seulement). */
  roomIds: string[];
  arrivalSlug: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Valide et normalise un profil saisi par l'admin ; message d'erreur sinon. */
export function normalizeAccessProfile(raw: unknown): AccessProfileInput | string {
  if (!raw || typeof raw !== 'object') return 'INVALID_PROFILE';
  const r = raw as Record<string, unknown>;
  const kind = r.kind === undefined ? 'limit' : r.kind;
  if (kind !== 'limit' && kind !== 'extra') return 'INVALID_KIND';
  const name = typeof r.name === 'string' ? r.name.trim() : '';
  if (!name || name.length > ACCESS_PROFILE_NAME_MAX) return 'INVALID_NAME';
  if (!Array.isArray(r.zoneSlugs)) return 'NO_ZONE';
  const zoneSlugs = [...new Set(r.zoneSlugs)];
  if (zoneSlugs.length > 50 || !zoneSlugs.every((s) => typeof s === 'string' && SLUG_RE.test(s))) return 'INVALID_ZONE';
  const rawRooms = r.roomIds === undefined ? [] : r.roomIds;
  if (!Array.isArray(rawRooms)) return 'INVALID_ROOM';
  const roomIds = [...new Set(rawRooms)];
  if (roomIds.length > 50 || !roomIds.every((id) => typeof id === 'string' && UUID_RE.test(id))) return 'INVALID_ROOM';
  // Un profil limitant ne réserve pas de salle ; un accès « en plus » peut ne réserver que des salles.
  if (kind === 'limit' && roomIds.length > 0) return 'INVALID_ROOM';
  if (zoneSlugs.length === 0 && roomIds.length === 0) return 'NO_ZONE';
  if (kind === 'limit' && zoneSlugs.length === 0) return 'NO_ZONE';
  const arrival = r.arrivalSlug == null || r.arrivalSlug === '' ? null : r.arrivalSlug;
  if (arrival !== null && (typeof arrival !== 'string' || !zoneSlugs.includes(arrival))) return 'INVALID_ARRIVAL';
  // Un accès « en plus » n'a pas de carte d'arrivée : on garde celle du campus ou du profil limitant.
  if (kind === 'extra') return { kind, name, zoneSlugs: zoneSlugs as string[], roomIds: roomIds as string[], arrivalSlug: null };
  return { kind, name, zoneSlugs: zoneSlugs as string[], roomIds: [], arrivalSlug: (arrival as string | null) ?? (zoneSlugs[0] as string) };
}
